/**
 * Dashboard específico para ADMINS
 * (super_admin, franchise_owner, admin)
 * - Solo ve datos de su franquicia (excepto super_admin)
 * - Gestión completa de franquicias, sucursales, barberos, servicios
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useFranchise } from '@/contexts/FranchiseContext';
import { useBranch } from '@/contexts/BranchContext';
import { useBarber } from '@/contexts/BarberContext';
import { useService } from '@/contexts/ServiceContext';
import { useOffer } from '@/contexts/OfferContext';
import { DashboardStats } from '@/components/dashboard/DashboardStats';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { LoadingState } from '@/components/shared/LoadingState';
import { OfferCard } from '@/components/offers/OfferCard';
import { Offer } from '@/types';
import { Users } from 'lucide-react';

export function AdminDashboard() {
  const { t } = useTranslation();
  const { user, customClaims } = useAuth();
  const { franchises, listFranchises } = useFranchise();
  const { branches, listBranches } = useBranch();
  const { barbers, listBarbers } = useBarber();
  const { services, listServices } = useService();
  const { getActiveOffer } = useOffer();
  const [loading, setLoading] = useState(true);
  const [activeOffer, setActiveOffer] = useState<Offer | null>(null);
  const [barberQueueCounts, setBarberQueueCounts] = useState<{ [barberId: string]: { count: number; hasInService: boolean } }>({});

  const userRole = customClaims?.role || user?.role;
  const isSuperAdmin = userRole === 'super_admin';

  // Load queue information for barbers
  useEffect(() => {
    const loadBarberQueueInfo = async () => {
      if (!barbers || barbers.length === 0) return;

      try {
        const counts: { [barberId: string]: { count: number; hasInService: boolean } } = {};

        // Load queue count for each barber
        for (const barber of barbers.slice(0, 5)) {
          const q = query(
            collection(db, 'queues'),
            where('barberId', '==', barber.barberId),
            where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service'])
          );

          const snapshot = await getDocs(q);
          const tickets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const hasInService = tickets.some((ticket: any) => ticket.status === 'in_service');

          counts[barber.barberId] = {
            count: snapshot.size,
            hasInService
          };
        }

        setBarberQueueCounts(counts);
      } catch (error) {
        console.error('Error loading barber queue info:', error);
      }
    };

    loadBarberQueueInfo();

    // Refresh every 30 seconds
    const interval = setInterval(loadBarberQueueInfo, 30000);
    return () => clearInterval(interval);
  }, [barbers]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        isSuperAdmin ? listFranchises() : Promise.resolve(), // Solo super_admin ve franquicias
        listBranches(),
        listBarbers(),
        listServices(),
      ]);

      // Load active offer for current franchise
      const franchiseId = user?.franchiseId || customClaims?.franchiseId;
      if (franchiseId) {
        const offer = await getActiveOffer(franchiseId);
        setActiveOffer(offer);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingState message={t('common.loading')} />;
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="container mx-auto px-3 py-4 md:p-6">
        {/* Header - Mobile optimized */}
        <div className="mb-6 md:mb-8">
          <h1 className="font-display text-2xl md:text-3xl text-ink mb-1 md:mb-2">
            {t('dashboard.greeting', { name: user?.displayName?.split(' ')[0] || 'User' })}
          </h1>
          <p className="text-sm md:text-base text-ink-muted">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Stats - Mobile: 2x2 grid */}
        <div className="mb-6 md:mb-8">
          <DashboardStats
            franchiseCount={isSuperAdmin ? (franchises?.length || 0) : 0}
            branchCount={branches?.length || 0}
            barberCount={barbers?.length || 0}
            serviceCount={services?.length || 0}
          />
        </div>

        {/* Quick Actions - Now mobile-first */}
        <QuickActions />

        {/* Barbers Status and Branches Overview */}
        <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Barbers Status */}
          <div>
            <h2 className="font-sans text-lg md:text-xl font-semibold text-ink mb-3 md:mb-6 px-1">{t('nav.barbers')}</h2>
            <div className="bg-surface border border-hairline rounded-xl overflow-hidden">
              {barbers && barbers.length > 0 ? (
                <div className="divide-y divide-hairline">
                  {barbers.slice(0, 5).map((barber) => {
                    const barberBranch = branches?.find(b => b.branchId === barber.branchId);
                    const queueInfo = barberQueueCounts[barber.barberId];
                    return (
                      <div key={barber.barberId} className="p-4 hover:bg-surface-2 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {barber.photoURL ? (
                              <img src={barber.photoURL} alt={barber.displayName} className="w-10 h-10 rounded-full object-cover ring-1 ring-hairline" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-surface-3 flex items-center justify-center text-ink-muted font-semibold">
                                {barber.displayName.charAt(0)}
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-ink">{barber.displayName}</p>
                              {barberBranch && (
                                <p className="text-xs text-ink-muted font-medium">
                                  {barberBranch.name}
                                </p>
                              )}
                              <p className="text-xs text-ink-subtle">
                                {barber.specialties.slice(0, 2).join(', ')}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {/* Availability status */}
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${barber.isAvailable ? 'bg-success' : 'bg-ink-subtle'}`} />
                              <span className={`text-xs font-medium ${
                                barber.isAvailable ? 'text-success' : 'text-ink-subtle'
                              }`}>
                                {barber.isAvailable ? t('barbers.status.available') : t('barbers.status.offline')}
                              </span>
                            </div>

                            {/* Busy indicator */}
                            {queueInfo?.hasInService && (
                              <div className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-danger" />
                                <span className="text-xs text-danger font-medium">
                                  Ocupado
                                </span>
                              </div>
                            )}

                            {/* Queue count */}
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3 text-ink-subtle" />
                              <span className="text-xs font-medium text-ink-muted tabular-nums">
                                {queueInfo ? `${queueInfo.count} ${queueInfo.count === 1 ? 'turno' : 'turnos'}` : '…'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-6 text-center text-ink-subtle">
                  <p>{t('barbers.empty')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Branches Overview */}
          <div>
            <h2 className="font-sans text-lg md:text-xl font-semibold text-ink mb-3 md:mb-6 px-1">{t('nav.branches')}</h2>
            <div className="bg-surface border border-hairline rounded-xl overflow-hidden">
              {branches && branches.length > 0 ? (
                <div className="divide-y divide-hairline">
                  {branches.slice(0, 5).map((branch) => (
                    <div key={branch.branchId} className="p-4 hover:bg-surface-2 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-ink">{branch.name}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                              branch.isActive
                                ? 'bg-success/10 text-success border-success/30'
                                : 'bg-surface-2 text-ink-muted border-hairline'
                            }`}>
                              {branch.isActive ? t('common.active') : t('common.inactive')}
                            </span>
                          </div>
                          <p className="text-xs text-ink-subtle">{branch.city}</p>
                          <p className="text-xs text-ink-subtle">{branch.address}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-ink-subtle">
                  <p>{t('branches.empty')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Services */}
        <div className="mt-6 md:mt-8">
          <h2 className="font-sans text-lg md:text-xl font-semibold text-ink mb-3 md:mb-6 px-1">{t('nav.services')}</h2>
          <div className="bg-surface border border-hairline rounded-xl overflow-hidden">
            {services && services.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {services.slice(0, 6).map((service) => (
                  <div key={service.serviceId} className="p-4 border border-hairline rounded-lg bg-surface-2 hover:border-accent/60 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-sans font-semibold text-ink">{service.name}</h3>
                      <span className="px-2 py-1 bg-surface-3 text-ink-muted text-xs rounded-full">
                        {service.category}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ink-muted tabular-nums">{service.duration} min</span>
                      <span className="font-semibold text-ink tabular-nums">€{(service.price / 100).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-ink-subtle">
                <p>{t('services.empty')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Active Offer */}
        {activeOffer && (
          <div className="mt-6 md:mt-8">
            <h2 className="font-sans text-lg md:text-xl font-semibold text-ink mb-3 md:mb-6 px-1">{t('offers.title')}</h2>
            <div className="max-w-lg">
              <OfferCard offer={activeOffer} compact={true} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
